// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Button, Spinner, Textarea, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import {
    AttachRegular,
    DismissCircle20Regular,
    MicRegular,
    MoreCircle24Regular,
    SendRegular,
    Warning20Regular,
} from '@fluentui/react-icons';
import debug from 'debug';
import * as speechSdk from 'microsoft-cognitiveservices-speech-sdk';
import React, { useRef } from 'react';
import { Constants } from '../../Constants';
import { AuthHelper } from '../../libs/auth/AuthHelper';
import { GetResponseOptions, useChat } from '../../libs/hooks/useChat';
import { AlertType } from '../../libs/models/AlertType';
import { ChatMessageType } from '../../libs/models/ChatMessage';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { addAlert } from '../../redux/features/app/appSlice';
import {
    editConversationInput,
    editConversationSiteSource,
    updateBotResponseStatus,
} from '../../redux/features/conversations/conversationsSlice';
import { Breakpoints, customTokens } from '../../styles';
import { Alerts } from '../shared/Alerts';
import { SpeechService } from './../../libs/services/SpeechService';
import { updateUserIsTyping } from './../../redux/features/conversations/conversationsSlice';
import { ChatStatus } from './ChatStatus';
import { ChatScope } from '../../redux/features/conversations/ChatState';
import { ISiteInfo } from '../../libs/models/SelectedSites';
import { SiteMap, buildSiteMap } from './tabs/SitesTab';

const log = debug(Constants.debug.root).extend('chat-input');

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.margin(0, '72px'),
    },
    typingIndicator: {
        maxHeight: '28px',
    },
    content: {
        ...shorthands.gap(tokens.spacingHorizontalM),
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
    },
    input: {
        width: '100%',
        ...shorthands.borderRadius(customTokens.borderRadiusNone),
        borderBottomLeftRadius: customTokens.borderRadiusMedium,
        borderBottomRightRadius: customTokens.borderRadiusMedium,
    },
    textarea: {
        maxHeight: '80px',
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
    },
    essentials: {
        display: 'flex',
        flexDirection: 'row',
        marginLeft: 'auto', // align to right
    },
    functional: {
        display: 'flex',
        flexDirection: 'row',
    },
    dragAndDrop: {
        ...shorthands.border(tokens.strokeWidthThick, ' solid', tokens.colorBrandStroke1),
        ...shorthands.padding('8px'),
        textAlign: 'center',
        backgroundColor: tokens.colorNeutralBackgroundInvertedDisabled,
        fontSize: tokens.fontSizeBase300,
        color: tokens.colorBrandForeground1,
        caretColor: 'transparent',
    },

    sitesPillsContainer: {
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        width: '95%',
        flexWrap: 'wrap',
    },
    sitesPillDesc: {
        marginRight: '10px',
        fontWeight: '600',
    },
    noSitesPillsContainer: {
        marginBottom: '10px',
        display: 'flex',
        alignItems: 'center',
        width: '95%',
    },
    noSitesPillDesc: {
        marginLeft: '10px',
        fontWeight: '600',
        color: 'red',
    },
    sitesPills: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    sitesPill: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        alignItems: 'center',
        ...shorthands.margin('8px', '10px', '5px', '0'),
        opacity: 1,
        ...shorthands.padding('1px', '4px', '1px', 0),
        ...shorthands.borderRadius('20px'),
        width: 'auto',
        color: '#ffffff',
        textAlign: 'center',
    },
    sitesPillValue: {
        ...shorthands.flex(1, 0, 'auto'),
        lineHeight: '18px',
        ...shorthands.padding('0px', '5px', '1px', '10px'),
        fontWeight: 400,
        ...Breakpoints.small({
            fontSize: '12px',
        }),
    },
    sitesPillIcon: {
        height: '20px',
        cursor: 'pointer',

        '&:hover': {
            '> svg': {
                color: '#ffffff',
            },
        },
    },
    moreArchivePills: {
        marginLeft: '5px',
        ...shorthands.padding('4px', '0', '0px', '0'),
        color: 'black',
        cursor: 'pointer',
        fontSize: '30px',

        '&:hover': {
            '> svg': {
                color: customTokens.colorBrandBackground,
            },
        },
    },
});

interface ChatInputProps {
    isDraggingOver?: boolean;
    onDragLeave: React.DragEventHandler<HTMLDivElement | HTMLTextAreaElement>;
    onSubmit: (options: GetResponseOptions) => Promise<void>;
}

export const ChatInput: React.FC<ChatInputProps> = ({ isDraggingOver, onDragLeave, onSubmit }) => {
    const classes = useClasses();
    const { instance, inProgress } = useMsal();
    const chat = useChat();
    const dispatch = useAppDispatch();
    const [value, setValue] = React.useState('');
    const [recognizer, setRecognizer] = React.useState<speechSdk.SpeechRecognizer>();
    const [isListening, setIsListening] = React.useState(false);
    const [documentImporting, setDocumentImporting] = React.useState(false);
    const documentFileRef = useRef<HTMLInputElement | null>(null);
    const { conversations, selectedId } = useAppSelector((state: RootState) => state.conversations);
    const { activeUserInfo } = useAppSelector((state: RootState) => state.app);
    const chatState = conversations[selectedId];
    const [pillsToShow, setPillsToShow] = React.useState(5);
    const [selectedVisibleSites, setSelectedVisibleSites] = React.useState<ISiteInfo[]>([] as ISiteInfo[]);

    const textAreaRef = React.useRef<HTMLTextAreaElement>(null);

    React.useEffect(() => {
        // Focus on the text area when the selected conversation changes
        textAreaRef.current?.focus();
    }, [selectedId]);

    React.useEffect(() => {
        if (!chatState.selectedSites) {
            setSelectedVisibleSites([]);
            return;
        }

        const selectedSites = chatState.selectedSites ?? ([] as ISiteInfo[]);
        const siteMap: SiteMap = buildSiteMap(selectedSites);
        const sites = selectedSites.filter((item) => Object.keys(siteMap).includes(item.siteUrl));

        setSelectedVisibleSites(sites);
    }, [chatState.selectedSites, selectedId]);

    React.useEffect(() => {
        async function initSpeechRecognizer() {
            const speechService = new SpeechService(process.env.REACT_APP_BACKEND_URI as string);
            const response = await speechService.getSpeechTokenAsync(
                await AuthHelper.getSKaaSAccessToken(instance, inProgress),
            );
            if (response.isSuccess) {
                const recognizer = speechService.getSpeechRecognizerAsyncWithValidKey(response);
                setRecognizer(recognizer);
            }
        }

        initSpeechRecognizer().catch((e) => {
            const errorDetails = e instanceof Error ? e.message : String(e);
            const errorMessage = `Unable to initialize speech recognizer. Details: ${errorDetails}`;
            dispatch(addAlert({ message: errorMessage, type: AlertType.Error }));
        });
    }, [dispatch, instance, inProgress]);

    React.useEffect(() => {
        const chatState = conversations[selectedId];
        setValue(chatState.input);
    }, [conversations, selectedId]);

    const handleSpeech = () => {
        setIsListening(true);
        if (recognizer) {
            recognizer.recognizeOnceAsync((result) => {
                if (result.reason === speechSdk.ResultReason.RecognizedSpeech) {
                    if (result.text && result.text.length > 0) {
                        handleSubmit(result.text);
                    }
                }
                setIsListening(false);
            });
        }
    };

    const handleImport = (dragAndDropFiles?: FileList) => {
        const files = dragAndDropFiles ?? documentFileRef.current?.files;

        if (files && files.length > 0) {
            setDocumentImporting(true);
            // Deep copy the FileList into an array so that the function
            // maintains a list of files to import before the import is complete.
            const filesArray = Array.from(files);
            void chat.importDocument(selectedId, filesArray).finally(() => {
                setDocumentImporting(false);
            });
        }

        // Reset the file input so that the onChange event will
        // be triggered even if the same file is selected again.
        if (documentFileRef.current?.value) {
            documentFileRef.current.value = '';
        }
    };

    const handleSubmit = (value: string, messageType: ChatMessageType = ChatMessageType.Message) => {
        if (value.trim() === '') {
            return; // only submit if value is not empty
        }

        setValue('');
        dispatch(editConversationInput({ id: selectedId, newInput: '' }));
        dispatch(updateBotResponseStatus({ chatId: selectedId, status: 'InfraRed GPT typing' }));
        onSubmit({ value, messageType, chatId: selectedId })
            .then(async () => {
                const chatState = conversations[selectedId];
                const selectedSites = chatState.selectedSites ?? ([] as ISiteInfo[]);
                await chat.editChat(
                    selectedId,
                    '',
                    chatState.systemDescription,
                    chatState.memoryBalance,
                    chatState.chatScope,
                    chatState.isHidden,
                    selectedSites,
                );
            })
            .catch((error) => {
                const message = `Error submitting chat input: ${(error as Error).message}`;
                log(message);
                dispatch(
                    addAlert({
                        type: AlertType.Error,
                        message,
                    }),
                );
            });
    };

    const handleDrop = (e: React.DragEvent<HTMLTextAreaElement>) => {
        onDragLeave(e);
        handleImport(e.dataTransfer.files);
    };

    const updateConversationSiteSource = async (updatedSelectedSites: ISiteInfo[]) => {
        await chat.editChat(
            selectedId,
            chatState.title,
            chatState.systemDescription,
            chatState.memoryBalance,
            chatState.chatScope,
            chatState.isHidden,
            updatedSelectedSites,
        );

        dispatch(
            editConversationSiteSource({
                id: selectedId,
                siteSources: updatedSelectedSites,
            }),
        );
    };

    const onRemoveSelection = (selectedSiteUrl: string) => {
        let updatedSelectedSites: ISiteInfo[] = [] as ISiteInfo[];

        const selectedSites = chatState.selectedSites ?? ([] as ISiteInfo[]);
        const siteMap: SiteMap = buildSiteMap(selectedSites);

        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete siteMap[selectedSiteUrl];

        const filteredSites = selectedSites.filter((item) => Object.keys(siteMap).includes(item.siteUrl));

        if (typeof filteredSites !== 'undefined' && filteredSites.length > 0) {
            updatedSelectedSites = filteredSites;
        }
        updateConversationSiteSource(updatedSelectedSites).catch((error) => {
            log('An error occured whilst editing chat state', error);
        });
    };

    return (
        <div className={classes.root}>
            <div className={classes.typingIndicator}>
                <ChatStatus />
            </div>
            {chatState.chatScope === ChatScope.SharePoint && selectedVisibleSites.length === 0 && (
                <div className={classes.noSitesPillsContainer}>
                    <Warning20Regular color="red" />
                    <div className={classes.noSitesPillDesc}>
                        You haven&apos;t not selected any sites. All Sharepoint sites will be used by GPT to gather
                        information.
                    </div>
                </div>
            )}
            {chatState.chatScope === ChatScope.SharePoint && selectedVisibleSites.length > 0 && (
                <div className={classes.sitesPillsContainer}>
                    <div className={classes.sitesPillDesc}>{`Selected site${
                        selectedVisibleSites.length > 1 ? 's' : ''
                    }:`}</div>
                    <div className={classes.sitesPills}>
                        {selectedVisibleSites.slice(0, pillsToShow).map((site) => (
                            <div
                                key={site.siteId}
                                className={classes.sitesPill}
                                style={{ backgroundColor: tokens.colorBrandBackground }}
                            >
                                <div className={classes.sitesPillValue}>{site.siteName}</div>
                                <div
                                    className={classes.sitesPillIcon}
                                    onClick={() => {
                                        onRemoveSelection(site.siteUrl);
                                    }}
                                >
                                    <DismissCircle20Regular color={'#ffffff'} />
                                </div>
                            </div>
                        ))}
                        {pillsToShow < selectedVisibleSites.length && (
                            <div
                                title="Show more"
                                className={classes.moreArchivePills}
                                onClick={() => {
                                    setPillsToShow(pillsToShow + 5);
                                }}
                            >
                                <MoreCircle24Regular />
                            </div>
                        )}
                    </div>
                </div>
            )}
            <Alerts />
            <div className={classes.content}>
                <Textarea
                    ref={textAreaRef}
                    id="chat-input"
                    resize="vertical"
                    textarea={{
                        className: isDraggingOver
                            ? mergeClasses(classes.dragAndDrop, classes.textarea)
                            : classes.textarea,
                    }}
                    className={classes.input}
                    value={isDraggingOver ? 'Drop your files here' : value}
                    onDrop={handleDrop}
                    onFocus={() => {
                        // update the locally stored value to the current value
                        const chatInput = document.getElementById('chat-input');
                        if (chatInput) {
                            setValue((chatInput as HTMLTextAreaElement).value);
                        }
                        // User is considered typing if the input is in focus
                        dispatch(
                            updateUserIsTyping({ userId: activeUserInfo?.id, chatId: selectedId, isTyping: true }),
                        );
                    }}
                    onChange={(_event, data) => {
                        if (isDraggingOver) {
                            return;
                        }

                        setValue(data.value);
                        dispatch(editConversationInput({ id: selectedId, newInput: data.value }));
                    }}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' && !event.shiftKey) {
                            event.preventDefault();
                            handleSubmit(value);
                        }
                    }}
                    onBlur={() => {
                        // User is considered not typing if the input is not  in focus
                        dispatch(
                            updateUserIsTyping({ userId: activeUserInfo?.id, chatId: selectedId, isTyping: false }),
                        );
                    }}
                />
            </div>
            <div className={classes.controls}>
                <div className={classes.functional}>
                    {/* Hidden input for file upload. Only accept .txt and .pdf files for now. */}
                    <input
                        type="file"
                        ref={documentFileRef}
                        style={{ display: 'none' }}
                        accept=".txt,.pdf,.md,.jpg,.jpeg,.png,.tif,.tiff,.doc,.docx,.ppt,.pptx"
                        multiple={true}
                        onChange={() => {
                            handleImport();
                        }}
                    />
                    <Button
                        disabled={documentImporting}
                        appearance="transparent"
                        icon={<AttachRegular />}
                        onClick={() => documentFileRef.current?.click()}
                    />
                    {documentImporting && <Spinner size="tiny" />}
                </div>
                <div className={classes.essentials}>
                    {recognizer && (
                        <Button
                            appearance="transparent"
                            disabled={isListening}
                            icon={<MicRegular />}
                            onClick={handleSpeech}
                        />
                    )}
                    <Button
                        appearance="transparent"
                        icon={<SendRegular />}
                        onClick={() => {
                            handleSubmit(value);
                        }}
                    />
                </div>
            </div>
        </div>
    );
};
